import React, { useState, useEffect } from 'react'
import { ColumnDefinition } from 'common/components/DataTable'
import GenericList from 'common/components/GenericList'
import FilterBox from "common/components/FilterBox"
import Utils from "common/Utils"
import { Button, Form } from 'react-bootstrap'
import EditMovementWorkflow from 'application/movements/EditMovementWorkflow'
import BulkEditMovementsWorkflow from 'application/movements/BulkEditMovementsWorkflow'
import * as Icon from 'react-bootstrap-icons'
import API from 'common/logic/API'
import OperationWorkflowModal from 'common/components/OperationWorkflowModal'
import { useNavigate } from 'react-router-dom'
import FilterModal from 'application/movements/FilterModal'
import { Movement, Fund, Tag, MovementFilterNode } from 'types'
import { encodeFilterTree, decodeFilterTree, filterTreeToString } from 'common/logic/FilterTree'

// Legacy flat params kept for backward-compat API querying
const expectedFilters = ["filter", "text", "start_date", "end_date", "fund", "amount_lt", "amount_gt", "amount_eq", "tags", "tag_mode", "no_tags"]
interface Props {
    url: string
    query: Record<string, string>
}

function MovementsList({ url, query: initialQuery }: Props) {
    const navigate = useNavigate()
    const [modal, setModal] = useState<{ type: string; id?: number; name?: string; movement?: Movement; filters?: Record<string, string>; filterTree?: MovementFilterNode | null; ids?: number[] } | false>(false)
    const [dataKey, setDataKey] = useState(Date.now())
    const [query, setQuery] = useState(initialQuery)
    const [funds, setFunds] = useState<Record<number, Fund> | null>(null)
    const [tags, setTags] = useState<Record<number, Tag> | null>(null)
    const [selectedIds, setSelectedIds] = useState<number[]>([])

    useEffect(() => {
        API.get("/funds").then((response) => {
            const results = (response.data as any)["results"] as Fund[]
            if (results) {
                setFunds(Object.fromEntries(results.map(x => [x.id, x])) as any)
            }
        }).catch(() => { })

        API.get("/tags").then((response) => {
            const results = (response.data as any)["results"] as Tag[]
            if (results) {
                setTags(Object.fromEntries(results.map(x => [x.id, x])) as any)
            }
        }).catch(() => { })
    }, [])

    const hideDialogs = () => setModal(false)
    const refreshData = () => setDataKey(Date.now())

    const onUpdateFilters = (filters: Record<string, unknown>) => {
        const prevQuery: Record<string, string> = { ...query }
        expectedFilters.forEach((f) => delete prevQuery[f])
        const newQuery = { ...prevQuery, ...filters } as Record<string, string>
        navigate(Utils.urlWithParameters(url, newQuery))
        setQuery(newQuery)
    }

    const showDeletionDialog = (id: number, name: string) => setModal({ type: "delete", id, name })
    const showCreateDialog = () => setModal({ type: "create" })
    const showEditDialog = (id: number) => setModal({ type: "edit", id })
    const showDuplicateDialog = (movement: Movement) => setModal({ type: "duplicate", movement })
    const showBulkEditDialog = () => setModal({ type: "bulkEdit" })
    const showBulkEditSelectedDialog = (ids: number[]) => setModal({ type: "bulkEditSelected", ids })
    const showBulkDeleteSelectedDialog = (ids: number[]) => setModal({ type: "bulkDeleteSelected", ids })
    const showFilterDialog = () => {
        const currentTree = query.filter ? decodeFilterTree(query.filter) : null
        setModal({ type: "filter", filters: query, filterTree: currentTree })
    }

    const deletionModal = (params: { id: number; name: string }) => {
        const confirmationStep = () => `Delete movement "${params.name}"?`
        const executeStep = () => API.delete(`/movements/${params.id}`)
        const completion = (result: unknown) => {
            hideDialogs()
            if (result) refreshData()
        }
        return <OperationWorkflowModal
            confirmationStep={confirmationStep}
            executionStep={executeStep}
            completionCallback={completion}
        />
    }

    const creationOrEditModal = (params: { id?: number; prefill?: Partial<Movement> }) => {
        const completion = (result: unknown) => {
            hideDialogs()
            if (result) setDataKey(Date.now())
        }
        return <EditMovementWorkflow onSuccess={completion} entryId={params.id} prefill={params.prefill} />
    }

    const filterTreeModal = (params: { filterTree: MovementFilterNode | null }) => {
        const completion = (result: MovementFilterNode | null) => {
            hideDialogs()
            const newFilters: Record<string, string | undefined> = { ...query }
            // Clear all old flat filter params when applying a new tree filter
            const oldFilterKeys = ["text", "start_date", "end_date", "fund", "amount_lt", "amount_gt", "amount_eq", "tags", "tag_mode", "no_tags"]
            oldFilterKeys.forEach(k => delete newFilters[k])
            if (result !== null) {
                newFilters.filter = encodeFilterTree(result)
            } else {
                delete newFilters.filter
            }
            onUpdateFilters(newFilters as Record<string, unknown>)
        }
        return <FilterModal onSuccess={completion} currentFilter={params.filterTree} funds={funds} tags={tags} />
    }

    let modalEl: React.ReactNode = null
    if (modal) {
        switch ((modal as any).type) {
            case "delete":
                modalEl = deletionModal(modal as { id: number; name: string })
                break
            case "create":
            case "edit":
                modalEl = creationOrEditModal(modal as { id?: number })
                break
            case "duplicate":
                modalEl = creationOrEditModal({ prefill: (modal as any).movement })
                break
            case "bulkEdit":
                modalEl = <BulkEditMovementsWorkflow
                    query={query}
                    onSuccess={(result) => { hideDialogs(); if (result) refreshData() }}
                />
                break
            case "bulkEditSelected":
                modalEl = <BulkEditMovementsWorkflow
                    query={query}
                    ids={(modal as any).ids}
                    onSuccess={(result) => { hideDialogs(); if (result) { setSelectedIds([]); refreshData() } }}
                />
                break
            case "bulkDeleteSelected": {
                const ids = (modal as any).ids as number[]
                const confirmationStep = () => `Delete ${ids.length} selected movement${ids.length !== 1 ? 's' : ''}?`
                const executeStep = () => API.delete('/movements/bulk', { ids })
                const completion = (result: unknown) => {
                    hideDialogs()
                    if (result) { setSelectedIds([]); refreshData() }
                }
                modalEl = <OperationWorkflowModal
                    confirmationStep={confirmationStep}
                    executionStep={executeStep}
                    completionCallback={completion}
                />
                break
            }
            case "filter":
                modalEl = filterTreeModal(modal as { filterTree: MovementFilterNode | null })
                break
            default:
        }
    }

    const searchParams: Record<string, string | undefined> = {}
    expectedFilters.forEach((f) => { searchParams[f] = query[f] })

    const activeFilterTree = query.filter ? decodeFilterTree(query.filter) : null
    const filterLabel = activeFilterTree ? filterTreeToString(activeFilterTree, funds, tags) : undefined

    const currentTextFilter = (activeFilterTree?.kind === 'all' && activeFilterTree.children.length === 1 &&
           activeFilterTree.children[0].kind === 'leaf' && activeFilterTree.children[0].type === 'TEXT')
        ? activeFilterTree.children[0].parameter
        : ''
    const [textInputValue, setTextInputValue] = useState(currentTextFilter)
    useEffect(() => { setTextInputValue(currentTextFilter) }, [currentTextFilter]) // eslint-disable-line react-hooks/exhaustive-deps

    const applyTextFilter = (text: string) => {
        const trimmed = text.trim()
        const newFilters: Record<string, unknown> = { ...query }
        delete (newFilters as any).filter
        if (trimmed !== '') {
            const leaf: MovementFilterNode = { kind: 'leaf', type: 'TEXT', parameter: trimmed }
            const tree: MovementFilterNode = { kind: 'all', children: [leaf] }
            newFilters.filter = encodeFilterTree(tree)
        }
        onUpdateFilters(newFilters)
    }

    const linkToFund = (obj: Movement) => {
        const tree: MovementFilterNode = { kind: 'leaf', type: 'FUND', parameter: String(obj.fund.id) }
        const linkUrl = Utils.urlWithParameters('/movements', null, { filter: encodeFilterTree(tree) })
        return <a href={linkUrl}>{obj.fund.name}</a>
    }

    const definitions = [
        new ColumnDefinition<Movement>("Date", obj => <>
            {obj.date}
            {obj.accountableDate !== obj.date && <><br /><small className="text-muted">{obj.accountableDate}</small></>}
        </>),
        new ColumnDefinition<Movement>("Summary", obj => {
            const space = obj.description && obj.statementEntry ? <br /> : ''
            return <>
                <strong>{obj.description}</strong>{space}{obj.statementEntry}
                {obj.notes && <><br /><small className="text-muted">{obj.notes}</small></>}
            </>
        }, "td-maxquarter"),
        new ColumnDefinition<Movement>("Amount", obj => <>{Utils.formatMoney(obj.amount, true)} {obj.fund.currency.name}</>, "td-currency", "td-right"),
        new ColumnDefinition<Movement>("Fund", linkToFund),
        new ColumnDefinition<Movement>("Tags", obj =>
            obj.tags && obj.tags.length > 0
                ? <>{obj.tags.map(t => <span key={t.id} className="badge badge-secondary mr-1">{t.name}</span>)}</>
                : null
        ),
        new ColumnDefinition<Movement>("Actions", obj => {
            return <>
                <Button variant="link" title="Edit"
                    onClick={() => showEditDialog(obj.id)}><Icon.Pencil /></Button> |
                <Button variant="link" title="Duplicate"
                    onClick={() => showDuplicateDialog(obj)}><Icon.Files /></Button> |
                <Button variant="link" title="Delete"
                    onClick={() => showDeletionDialog(obj.id, obj.statementEntry || obj.description || '')}><Icon.Trash /></Button>
            </>
        })
    ]

    const clearFilter = () => {
        const newFilters: Record<string, unknown> = { ...query }
        delete (newFilters as any).filter
        onUpdateFilters(newFilters)
    }

    const filterBox = <FilterBox
            filterLabel={filterLabel}
            onClearFilter={clearFilter}
            onEditFilter={showFilterDialog}
        />

    const filterButton = <Button variant="link" className="p-0" onClick={showFilterDialog}><Icon.Search /> Filter</Button>

    const bulkActions = selectedIds.length > 0
        ? <>
            <Button variant="link" className="p-0 mr-2" onClick={() => showBulkEditSelectedDialog(selectedIds)}>Edit selected ({selectedIds.length})</Button>/
            <Button variant="link" className="p-0 ml-2" onClick={() => showBulkDeleteSelectedDialog(selectedIds)}>Delete selected ({selectedIds.length})</Button>
          </>
        : null

    return (
        <>{modalEl}
            <GenericList
                title="Movements"
                columns={definitions as unknown as ColumnDefinition[]}
                endpoint={url}
                endpointParams={searchParams as Record<string, unknown>}
                beforeTable={<>
                    <p>
                        <Button variant="link" onClick={showCreateDialog}>Add new</Button> |
                        <Button variant="link" onClick={() => navigate("/movements/import")}>Import</Button> |
                        <Button variant="link" onClick={showBulkEditDialog}>Edit all matching filter</Button>
                        {!filterLabel && <> | {filterButton}</>} |
                        <Form.Control
                            size="sm"
                            type="text"
                            placeholder="Search…"
                            value={textInputValue}
                            onChange={e => setTextInputValue(e.target.value)}
                            onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Enter') applyTextFilter(textInputValue) }}
                            style={{ width: '180px', display: 'inline-block' }}
                        />
                    </p>
                    {filterLabel ? filterBox : undefined}
                </>}
                dataKey={dataKey}
                selectable={true}
                onSelectionChange={setSelectedIds}
                paginationExtra={bulkActions}
            />
        </>
    )
}

export default MovementsList
